import { Ref } from "react";
import WebConfig from "middlewares/WebConfig";
import store from "../../index";
import responseHandler from "responseHandler";

interface WebApiBlobRequestParams {
  method: string;
  endpoint: string;
  payload?: any;
  successCallback: (blob: Blob) => void;
  errorCallback?: () => void;
  isFetchingCallback?: (isFetching: boolean) => void;
  mountedRef?: Ref<boolean>;
  disableDefaultErrorResponseHandler?: boolean;
}

/**
 * Authenticated web API request that returns a Blob response.
 * Follows the same conventions as WebApiRequest (auth headers, mountedRef guard,
 * webApiRoot prefixing, error handling) but parses the response as a binary blob
 * instead of JSON.
 */
export const WebApiBlobRequest = async ({
  method,
  endpoint,
  payload = null,
  successCallback,
  errorCallback = null,
  isFetchingCallback = null,
  mountedRef = null,
  disableDefaultErrorResponseHandler = false,
}: WebApiBlobRequestParams) => {
  const isMounted = () => !mountedRef || (mountedRef as React.RefObject<boolean>).current;

  if (isFetchingCallback && isMounted()) isFetchingCallback(true);

  let url = endpoint;
  if (!url.startsWith("http")) {
    url = WebConfig.webApiRoot + url;
  }

  const headers: HeadersInit = {};
  if (store) {
    headers["Authorization"] = `Bearer ${store.getState().user.profile.token}`;
  }

  const requestInit: RequestInit = {
    method,
    headers,
    credentials: "same-origin",
  };

  if (payload !== null) {
    headers["Content-Type"] = "application/json";
    requestInit.body = JSON.stringify(payload);
  }

  try {
    const response = await fetch(url, requestInit);

    if (!response.ok) {
      throw response;
    }

    const blob = await response.blob();

    if (isMounted()) {
      successCallback(blob);
      if (isFetchingCallback) isFetchingCallback(false);
    }
  } catch (error) {
    if (!disableDefaultErrorResponseHandler) {
      responseHandler.error(error, store.dispatch);
    }
    if (isMounted()) {
      if (errorCallback) errorCallback();
      if (isFetchingCallback) isFetchingCallback(false);
    }
  }
};
